import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ExternalLink, Link2, Link2Off, Loader2, Send, CheckCircle2, XCircle } from "lucide-react";

interface TelegramConnection {
  id: string;
  telegram_chat_id: number;
  telegram_username: string | null;
  telegram_first_name: string | null;
  enabled: boolean;
  created_at: string;
}

const TelegramConnector = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [connection, setConnection] = useState<TelegramConnection | null>(null);
  const [loadingConnection, setLoadingConnection] = useState(true);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [botUsername, setBotUsername] = useState<string>("");
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchConnection = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("telegram_connections")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      console.error("Failed to fetch telegram connection:", error);
    } else {
      setConnection(data as TelegramConnection | null);
    }
    setLoadingConnection(false);
  };

  useEffect(() => {
    fetchConnection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setGeneratingLink(true);
    setDeepLink(null);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();

      const res = await fetch(`${supabaseUrl}/functions/v1/telegram-link`, {
        method: "POST",
        headers,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to generate link" }));
        throw new Error(err.error || "Failed to generate link");
      }

      const { deep_link, bot_username } = await res.json();
      setDeepLink(deep_link);
      setBotUsername(bot_username);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user) return;
    setDisconnecting(true);
    const { error } = await supabase
      .from("telegram_connections")
      .delete()
      .eq("user_id", user.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setConnection(null);
      setDeepLink(null);
      toast({ title: "Disconnected", description: "Your Telegram account has been unlinked." });
    }
    setDisconnecting(false);
    setDisconnectOpen(false);
  };

  if (loadingConnection) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold">Telegram Integration</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Link your Telegram account to chat with your data connectors directly from Telegram.
        </p>
      </div>

      {/* Connection status card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Telegram Account
            </CardTitle>
            {connection ? (
              <Badge variant="default" className="gap-1.5">
                <CheckCircle2 className="h-3 w-3" />
                Connected
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1.5">
                <XCircle className="h-3 w-3" />
                Not connected
              </Badge>
            )}
          </div>
          <CardDescription>
            {connection
              ? "Your Telegram account is linked. You can chat with the bot to query your data sources."
              : "Connect your Telegram account to chat with your data connectors via the Telegram bot."}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          {connection ? (
            <>
              <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm space-y-1">
                {connection.telegram_first_name && (
                  <p>
                    <span className="font-medium">Name:</span>{" "}
                    {connection.telegram_first_name}
                  </p>
                )}
                {connection.telegram_username && (
                  <p>
                    <span className="font-medium">Username:</span>{" "}
                    @{connection.telegram_username}
                  </p>
                )}
                <p>
                  <span className="font-medium">Chat ID:</span>{" "}
                  {connection.telegram_chat_id}
                </p>
                <p>
                  <span className="font-medium">Linked:</span>{" "}
                  {new Date(connection.created_at).toLocaleDateString()}
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDisconnectOpen(true)}
                  className="gap-1.5"
                >
                  <Link2Off className="h-4 w-4" />
                  Disconnect
                </Button>
              </div>
            </>
          ) : (
            <>
              {deepLink ? (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-muted-foreground">
                    Tap the button below to open Telegram and link your account. The link
                    expires in <strong>15 minutes</strong>.
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button asChild className="gap-1.5">
                      <a href={deepLink} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                        Open in Telegram
                      </a>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(deepLink);
                        toast({ title: "Copied", description: "Link copied to clipboard." });
                      }}
                    >
                      Copy Link
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleConnect}
                      disabled={generatingLink}
                    >
                      Regenerate
                    </Button>
                  </div>
                  {botUsername && (
                    <p className="text-xs text-muted-foreground">
                      Bot:{" "}
                      <a
                        href={`https://t.me/${botUsername}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        @{botUsername}
                      </a>
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    After linking, click{" "}
                    <button
                      className="underline"
                      onClick={fetchConnection}
                    >
                      Refresh
                    </button>{" "}
                    to see your connection status.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-muted-foreground">
                    Click <strong>Connect Telegram</strong> to generate a one-time link. Open
                    it in Telegram to complete the connection.
                  </p>
                  <Button
                    onClick={handleConnect}
                    disabled={generatingLink}
                    className="w-fit gap-1.5"
                  >
                    {generatingLink ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Link2 className="h-4 w-4" />
                    )}
                    Connect Telegram
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">How it works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Click <strong>Connect Telegram</strong> above to generate a linking link.</li>
            <li>Tap the link — it will open Telegram with the bot pre-loaded.</li>
            <li>The bot confirms the link and your account is connected.</li>
            <li>
              Send any database question to the bot (e.g. <em>"Show me today's sales"</em>).
            </li>
            <li>The bot queries your configured data sources and replies with the results.</li>
          </ol>
        </CardContent>
      </Card>

      {/* Disconnect confirm */}
      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Telegram?</AlertDialogTitle>
            <AlertDialogDescription>
              Your Telegram account will be unlinked. You won't be able to chat with your
              data sources from Telegram until you reconnect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnect} disabled={disconnecting}>
              {disconnecting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TelegramConnector;
